import { useEffect, useState } from "react";
import { useStore } from "@/hooks/useStore";
import { Panel, PageTitle } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface ProfileRow {
  id: string;
  display_name: string | null;
  email: string | null;
  role: "admin" | "player";
}

export function AdminPanel() {
  const { state, reload } = useStore();
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(false);

  async function loadProfiles() {
    setLoading(true);
    // Order by email so the list is stable even when display_name is NULL
    // (display_name is NULL for accounts that never set one). Email is the
    // primary identifier shown in the list.
    const { data, error } = await supabase
      .from("profiles")
      .select("id, display_name, email, role")
      .order("email", { nullsFirst: false });
    if (error) toast.error(error.message);
    else setProfiles((data ?? []) as ProfileRow[]);
    setLoading(false);
  }

  useEffect(() => {
    loadProfiles();
  }, []);

  async function setRole(id: string, role: "admin" | "player") {
    // Returning the row lets us detect silent RLS denials — if the policy
    // blocks the update, Postgres returns 0 rows but no error. Without this
    // check the toast would falsely say "Role updated".
    const { data, error } = await supabase
      .from("profiles")
      .update({ role })
      .eq("id", id)
      .select("id");
    if (error) {
      toast.error(error.message);
      return;
    }
    if (!data || data.length === 0) {
      toast.error("Update blocked — you may not have admin permission");
      return;
    }
    toast.success(`Role updated to ${role}`);
    loadProfiles();
  }

  return (
    <div className="space-y-4">
      <PageTitle>Admin Panel</PageTitle>

      <div className="grid gap-3 sm:grid-cols-3">
        <Panel><p className="text-xs text-[#64748b]">Cards</p><p className="font-display text-2xl">{state.cards.length}</p></Panel>
        <Panel><p className="text-xs text-[#64748b]">Decks</p><p className="font-display text-2xl">{state.decks.length}</p></Panel>
        <Panel><p className="text-xs text-[#64748b]">Glossary</p><p className="font-display text-2xl">{state.referenceData.glossary.length}</p></Panel>
      </div>

      <Panel>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-display text-sm font-semibold">Profiles ({profiles.length})</h3>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={loadProfiles}>Refresh</Button>
            <Button variant="secondary" onClick={() => reload()}>Reload state</Button>
          </div>
        </div>
        {loading && <p className="text-xs text-[#64748b]">Loading…</p>}
        <div className="space-y-1">
          {profiles.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 border-b border-[#22262f] py-1">
              <div className="min-w-0 text-sm">
                {/* Email is the primary identifier; display_name is shown as a
                    secondary label when set. id prefix kept for debugging. */}
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="truncate font-medium">{p.email ?? "(no email)"}</span>
                  {p.display_name && (
                    <span className="truncate text-xs text-[#64748b]">{p.display_name}</span>
                  )}
                </div>
                <span className="text-xs text-[#64748b]">{p.id.slice(0, 8)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`gm-tag ${p.role === "admin" ? "border-amber/40 text-amber-soft" : ""}`}>{p.role}</span>
                <Button
                  size="sm"
                  variant={p.role === "admin" ? "secondary" : "primary"}
                  onClick={() => setRole(p.id, p.role === "admin" ? "player" : "admin")}
                >
                  Make {p.role === "admin" ? "player" : "admin"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
